import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Button } from '../atoms/Button';
import { useGetMerchantBlackListReasons, useUpdateMerchantStatusType } from '../../hooks/merchant';
import { useSetSnackBar } from '../../store/Snackbar.state';

export type BlacklistModalProps = {
  isOpen: boolean;
  onClose: () => void;
  merchantId: number | null;
  merchantName?: string;
  onSuccess?: () => void;
};

export const BlacklistModal: React.FC<BlacklistModalProps> = ({
  isOpen,
  onClose,
  merchantId,
  merchantName,
  onSuccess
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const setSnackbar = useSetSnackBar();

  const { data: blacklistReasonsData, isLoading: isReasonsLoading } = useGetMerchantBlackListReasons();
  const { mutate: updateMerchantStatus, isLoading: isUpdating } = useUpdateMerchantStatusType();

  const blacklistReasons = useMemo(() => {
    return blacklistReasonsData?.data?.map((reason) => ({
      label: reason.description,
      value: reason.description,
    })) || [];
  }, [blacklistReasonsData?.data]);

  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    setSelectedReasonId(event.target.value as string);
  };

  const handleSubmit = () => {
    if (!merchantId || !selectedReasonId) {
      setSnackbar({
        severity: "error",
        description: "Lütfen kara liste nedeni seçiniz.",
        isOpen: true,
      });
      return;
    }

    updateMerchantStatus(
      {
        merchantId: merchantId,
        processType: 1,
        blackListReason: selectedReasonId,
      },
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setSnackbar({
              severity: "success",
              description: data.message || "İşyeri başarıyla kara listeye eklendi.",
              isOpen: true,
            });
            onClose();
            setSelectedReasonId('');
            if (onSuccess) {
              onSuccess();
            }
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: (error: any) => {
          setSnackbar({
            severity: "error",
            description: error?.message || "İşlem sırasında bir hata oluştu.",
            isOpen: true,
          });
        },
      }
    );
  };

  const handleClose = () => {
    setSelectedReasonId('');
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Şirket Statüsünü Değiştir
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {merchantName && (
            <Typography variant="body1">
              <strong>İşyeri:</strong> {merchantName}
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary">
            İşyerini kara listeye eklemek için bir neden seçiniz:
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="blacklist-reason-label">Kara Liste Nedeni</InputLabel>
            <Select
              labelId="blacklist-reason-label"
              id="blacklist-reason-select"
              value={selectedReasonId}
              label="Kara Liste Nedeni"
              onChange={handleReasonChange}
              disabled={isReasonsLoading}
            >
              {blacklistReasons.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          text="İptal"
          variant="outlined"
          onClick={handleClose}
          disabled={isUpdating}
        />
        <Button
          text="Kara Listeye Ekle"
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={isUpdating || !selectedReasonId || isReasonsLoading}
          sx={{
            backgroundColor: '#f5004a',
            '&:hover': {
              backgroundColor: '#d3003f',
            }
          }}
        />
      </DialogActions>
    </Dialog>
  );
};